import { FormEventHandler, useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { skipToken } from "@reduxjs/toolkit/query/react";
import {
  FieldErrors,
  SubmitHandler,
  UseFormReturn,
  useForm,
} from "react-hook-form";
import { useParams } from "react-router";
import { SoftwareInfo } from "@sscs/types";
import { Toast } from "@sscs/ui";
import { getReleaseDateDisplay } from "@sscs/utils";
import { useGetVersionsQuery, useUpdateVersionMutation } from "Services";
import { FileType } from "Types";
import {
  SoftwareInfoWithReleased,
  apiErrorToast,
  getEditVersionSoftwareInfoFormSchema,
  getFileSoftwareInfoFormSchema,
} from "Utils";
import { useUpdateSoftwareInfoMutation } from "../../services/softwareInfo.api";

type UseEditSoftwareInfoFormProps = {
  onClose: () => void;
  fileId: string;
  fileType: FileType;
  softwareInfo: SoftwareInfo;
};

type UseEditSoftwareInfoForm = {
  errors: FieldErrors<SoftwareInfoWithReleased>;
  onSubmit: FormEventHandler<HTMLFormElement>;
  formMethods: UseFormReturn<SoftwareInfoWithReleased>;
};

const useEditSoftwareInfoForm = ({
  onClose,
  fileId,
  fileType,
  softwareInfo,
}: UseEditSoftwareInfoFormProps): UseEditSoftwareInfoForm => {
  const [updateFile] = useUpdateSoftwareInfoMutation();
  const [updateArtifact] = useUpdateVersionMutation();

  const isFileStream = fileType === "FILESTREAM";
  const isArtifact = fileType === "VERSION";

  const { packageId } = useParams();
  const { data } = useGetVersionsQuery(
    isArtifact && packageId ? { packageId } : skipToken,
  );

  const otherExistingVersions = useMemo(
    () =>
      (data?.versions ?? [])
        .map((version) => version.software_info.version)
        .filter((version) => version !== softwareInfo.version),
    [data, softwareInfo.version],
  );

  const defaultValues = useMemo(() => {
    return {
      product: softwareInfo.product,
      publisher: softwareInfo.publisher,
      version: softwareInfo.version,
      category: softwareInfo.category,
      license: softwareInfo.license,
      platform: softwareInfo.platform,
      architecture: softwareInfo.architecture,
      release_date: softwareInfo.release_date
        ? getReleaseDateDisplay(softwareInfo.release_date)
        : null,
      is_released: softwareInfo.release_date !== null,
    };
  }, [
    softwareInfo.category,
    softwareInfo.license,
    softwareInfo.platform,
    softwareInfo.architecture,
    softwareInfo.product,
    softwareInfo.publisher,
    softwareInfo.release_date,
    softwareInfo.version,
  ]);

  const formMethods = useForm<SoftwareInfoWithReleased>({
    resolver: yupResolver(
      fileType === "FILESTREAM"
        ? getFileSoftwareInfoFormSchema()
        : getEditVersionSoftwareInfoFormSchema(otherExistingVersions),
    ),
    defaultValues,
  });

  const {
    formState: { errors },
    handleSubmit,
  } = formMethods;

  const onSubmit: SubmitHandler<SoftwareInfo> = async (data) => {
    try {
      if (isFileStream) {
        await updateFile({
          id: fileId,
          softwareInfo: data,
        }).unwrap();
      } else if (isArtifact) {
        await updateArtifact({
          versionId: fileId,
          body: {
            is_released: data.release_date !== null,
            software_info: data,
          },
        }).unwrap();
      }
    } catch (error) {
      apiErrorToast(error, "Could not save changes");
      return;
    }

    Toast.show({
      title: `${isArtifact ? "Version" : "File"} details updated`,
      type: "success",
    });

    onClose();
  };

  return {
    onSubmit: handleSubmit(onSubmit),
    errors,
    formMethods,
  };
};

export default useEditSoftwareInfoForm;
